import { FC, useEffect, useState } from 'react';
import * as Styled from './ProfileButton.styled';
import { useAuth0 } from '@auth0/auth0-react';
import { AnimatedIcon, Icon, Loading, PopUpDialog } from '@maverick/ui';
import { useTheme } from 'styled-components';
import { GaEvent } from '@maverick/utils';
import { Config } from '../../../../Config';
import { retrieveArrowOffsetPosition, retrieveDisplayName, handleKeyDown } from './ProfileButton.utils';
import { useIsMobile } from '@maverick/hooks';
import { useRedirect } from '../../../../hooks/useRedirect';
import { AppOperations, RootState, UserOperations } from '@maverick/store';
import { useDispatch, useSelector } from 'react-redux';

const userPages = ['/recent-orders', '/my-account', '/faves', '/my-rewards', '/my-activity'];

export const ProfileButton: FC = () => {
    const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
    const router = useRedirect();
    const { orderUi } = useTheme();
    const [showedIcon, setShowedIcon] = useState<React.JSX.Element>(
        <Icon icon={orderUi.icons.iconUserHeader} customSize testId='user-icon' />
    );
    const [arrowOffsetPosition, setArrowOffsetPosition] = useState<number | undefined>();
    const isSmallScreen = useIsMobile(768);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const popUp_id = 'signIn-dialog';

    const { userLogged, userInfo } = useSelector((state: RootState) => state.user);

    const dispatch = useDispatch();
    const setUserLogged = (userLogged: boolean) => dispatch(UserOperations.setUserLogged(userLogged));
    const setUserAuthLastInteraction = (userAuthLastInteraction: string) =>
        dispatch(UserOperations.setUserAuthLastInteraction(userAuthLastInteraction));
    const setRedirectRoute = (redirect: string) => dispatch(AppOperations.setRedirectRoute(redirect));

    useEffect(() => {
        const onResize = () => {
            const arrowOffset = retrieveArrowOffsetPosition();
            setArrowOffsetPosition(arrowOffset);
        };

        setTimeout(() => {
            onResize();
        }, 1000);

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            if (userLogged !== isAuthenticated) {
                setShowedIcon(
                    <AnimatedIcon
                        icon={orderUi.icons.iconUserLoggedAnimation}
                        onAnimationComplete={handleAnimationComplete}
                        testId='user-icon'
                    />
                );
                setUserLogged(isAuthenticated);
            } else {
                setShowedIcon(<Icon icon={orderUi.icons.iconUserLoggedHeader} customSize testId='user-icon' />);
            }
        }
    }, [isAuthenticated]);

    const handleAnimationComplete = () => {
        setShowedIcon(<Icon icon={orderUi.icons.iconUserLoggedHeader} customSize testId='user-icon' />);
    };

    const GaTopNavigationEvent = (label: string) => {
        GaEvent.TopNavigation(label);
    };

    const handleLogin = (prompt: string) => {
        setUserAuthLastInteraction('navbar');
        if (isLoading) {
            return;
        }
        setRedirectRoute(location.pathname);
        if (prompt === 'login') {
            GaTopNavigationEvent('Sign in');
            loginWithRedirect({ source: Config.Source, prompt: 'login' });
        } else {
            GaTopNavigationEvent('Join Now');
            loginWithRedirect({ source: Config.Source });
        }
    };

    const renderPopUpContent = () => {
        return (
            <Styled.PopUpContainer data-testid='profile-button-popup'>
                <Styled.PopUpTitle id={`${popUp_id}-title`}>Sign in or join now</Styled.PopUpTitle>
                <Styled.PopUpText>
                    {'Sign in or join Dine Rewards now to save your favorites and quickly reorder.'}
                </Styled.PopUpText>
                <Styled.PopUpButtons>
                    <Styled.StyledLink
                        href='#'
                        variant='sign-in'
                        data-testid='sign-in'
                        onClick={() => handleLogin('login')}
                    >
                        Sign in
                    </Styled.StyledLink>
                    <Styled.StyledLink
                        href='#'
                        variant='join-now'
                        data-testid='join-now'
                        onClick={() => handleLogin('join-now')}
                    >
                        Join now
                    </Styled.StyledLink>
                </Styled.PopUpButtons>
            </Styled.PopUpContainer>
        );
    };

    const handleClickProfile = () => {
        if (!isAuthenticated) {
            setIsModalOpen(!isModalOpen);
            GaTopNavigationEvent('Login');
            return;
        }
        if (!userPages.includes(location.pathname)) {
            setRedirectRoute(location.pathname);
        }
        router('/my-account');
    };

    if (isAuthenticated) {
        return (
            <Styled.UserAccountLink
                href='/my-account'
                data-testid='user-account-container'
                id='user-account-container'
                aria-label={`${retrieveDisplayName(userInfo, isAuthenticated, isSmallScreen)}, view your account`}
                onClick={(e) => {
                    e.preventDefault();
                    if (!userPages.includes(location.pathname)) {
                        setRedirectRoute(location.pathname);
                    }
                    router('/my-account');
                }}
                onKeyDown={(ev) => handleKeyDown(ev, () => router('/my-account'))}
            >
                <Styled.SignIn>
                    <Styled.AnimatedIconContainer
                        id='animated-icon-container'
                        data-testid='animated-icon-container'
                        isLoading={isLoading}
                    >
                        {isLoading ? <Loading size={14} color='#fff' /> : showedIcon}
                    </Styled.AnimatedIconContainer>
                    <Styled.SignInText>
                        {retrieveDisplayName(userInfo, isAuthenticated, isSmallScreen)}
                    </Styled.SignInText>
                </Styled.SignIn>
            </Styled.UserAccountLink>
        );
    }

    return (
        <PopUpDialog
            id={popUp_id}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            content={renderPopUpContent()}
            arrowOffsetPosition={arrowOffsetPosition}
            centered={false}
            ariaLabelledBy={`${popUp_id}-title`}
        >
            <Styled.UserAccount
                data-testid='user-account-container'
                id='user-account-container'
                aria-label={retrieveDisplayName(userInfo, isAuthenticated, isSmallScreen)}
                onClick={handleClickProfile}
                aria-expanded={isModalOpen}
                aria-controls={`${popUp_id}-content`}
                aria-haspopup='dialog'
            >
                <Styled.SignIn>
                    <Styled.AnimatedIconContainer
                        id='animated-icon-container'
                        data-testid='animated-icon-container'
                        isLoading={isLoading}
                    >
                        {isLoading ? <Loading size={14} color='#fff' /> : showedIcon}
                    </Styled.AnimatedIconContainer>
                    <Styled.SignInText>
                        {retrieveDisplayName(userInfo, isAuthenticated, isSmallScreen)}
                    </Styled.SignInText>
                </Styled.SignIn>
            </Styled.UserAccount>
        </PopUpDialog>
    );
};
